/**
 * NEEDS A PERSON: the queue of customers who asked for a human.
 *
 * Anthony, 2026-09-28, for WeRV: "a queue for problem people who want to
 * talk to a human". Until now a hand-off from the intake agent rang the
 * bell, wrote NEEDS A PERSON on top of the lead's notes and emailed the
 * approvers. All three are loud once and then gone: the bell is cleared by
 * whoever glances at it, the note is one paragraph among forty, and the
 * email sits in one person's inbox. None of them answers the only question
 * the desk has at four in the afternoon, which is "who is still waiting on
 * one of us, and has anybody got them".
 *
 * So a hand-off now also opens a row in `human_queue` (migration 0184),
 * one per customer, that stays open until a PERSON closes it. The agent
 * picking the thread back up after fifteen minutes does not close it: the
 * agent carrying on is not the same as somebody having rung them.
 *
 * Pure, and in src/domain, because the browser and the server both have to
 * agree on what a valid phone, reason and note are, and on how long
 * "waiting 12 min" is. Nothing here reads a network.
 */

export type QueueSource = "intake" | "manual" | "front_desk";

/** A row as the API hands it to the page. */
export type QueueItem = {
  id: string;
  phone10: string | null;
  customer_name: string | null;
  reason: string;
  source: QueueSource;
  urgent: boolean;
  last_message: string | null;
  opened_at: string;
  claimed_by: string | null;
  claimed_name: string | null;
  claimed_at: string | null;
  closed_at: string | null;
  close_note: string | null;
};

/** How long each free-text field may be. The same caps the server writes with. */
export const LIMITS = { name: 120, reason: 300, message: 600, note: 500 } as const;

/**
 * Ten digits or nothing. A leading country code 1 is dropped, because
 * people paste "+1 (951) 555-0100" and "19515550100" as often as the ten.
 */
export function phone10Of(raw: unknown): string | null {
  let d = String(raw ?? "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  return d.length === 10 ? d : null;
}

/** Collapse whitespace, trim, cap. Empty stays empty. */
export function clean(raw: unknown, max: number): string {
  return String(raw ?? "").replace(/\s+/g, " ").trim().slice(0, max);
}

export type ManualItem = { phone10: string; customer_name: string | null; reason: string; urgent: boolean };

/**
 * WHAT "ADD SOMEONE" MAY WRITE.
 *
 * A phone is required, because the whole point of a card here is a number
 * somebody can press. A reason is required, because a card that only says
 * a number is a card the next person has to ring to find out why. The name
 * is optional: somebody who rang the landline and would not give one still
 * needs calling back.
 */
export function parseManual(body: unknown): { ok: true; value: ManualItem } | { ok: false; error: string } {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const phone10 = phone10Of(b["phone"]);
  if (!phone10) return { ok: false, error: "Put in a ten digit phone number." };
  const reason = clean(b["reason"], LIMITS.reason);
  if (!reason) return { ok: false, error: "Say why they need a person." };
  const name = clean(b["name"], LIMITS.name);
  return { ok: true, value: { phone10, customer_name: name || null, reason, urgent: b["urgent"] === true } };
}

/** The optional note on Done. Never required: "rang her, sorted" is often all there is. */
export function parseCloseNote(body: unknown): string | null {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  return clean(b["note"], LIMITS.note) || null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** An id from a URL, checked before it is put into a PostgREST filter. */
export const isQueueId = (s: unknown): s is string => typeof s === "string" && UUID.test(s);

/**
 * THE ROW A HAND-OFF WRITES, from what the intake agent knows at the time.
 *
 * `urgent` only ever goes UP while the item is open. A customer who was
 * stranded on the first hand-off and then asked a calmer question on the
 * second is still somebody who was stranded, and the red pill is how the
 * desk knows to ring them first.
 */
export function handoffFields(x: {
  reason: string; urgent: boolean; name?: string | null; lastMessage?: string | null;
  threadId?: string | null; wasUrgent?: boolean;
}): { reason: string; urgent: boolean; customer_name?: string; last_message: string | null; intake_thread_id: string | null } {
  const name = clean(x.name, LIMITS.name);
  return {
    reason: clean(x.reason, LIMITS.reason) || "Asked for a person",
    urgent: Boolean(x.urgent) || Boolean(x.wasUrgent),
    ...(name ? { customer_name: name } : {}),
    last_message: clean(x.lastMessage, LIMITS.message) || null,
    intake_thread_id: x.threadId ?? null,
  };
}

/**
 * "waiting 12 min", "waiting 2 hr 5 min", "waiting 3 days".
 *
 * Its own function rather than ui/dom.ts's ago(), because the page shows
 * this for things that have JUST opened and "waiting just now" is not a
 * sentence anybody says.
 */
export function waitingFor(openedAt: string, now: number = Date.now()): string {
  const t = Date.parse(openedAt);
  if (!Number.isFinite(t)) return "waiting";
  const mins = Math.max(0, Math.floor((now - t) / 60_000));
  if (mins < 1) return "waiting under a minute";
  if (mins < 60) return `waiting ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `waiting ${hrs} hr${mins % 60 ? ` ${mins % 60} min` : ""}`;
  const days = Math.floor(hrs / 24);
  return `waiting ${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Open ones first, urgent at the top, then whoever has waited longest.
 * The desk works this list from the top, so the top has to be the person
 * who most needs ringing.
 */
export function queueOrder(a: QueueItem, b: QueueItem): number {
  const ao = a.closed_at ? 1 : 0, bo = b.closed_at ? 1 : 0;
  if (ao !== bo) return ao - bo;
  if (a.closed_at && b.closed_at) return b.closed_at.localeCompare(a.closed_at);
  if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
  return a.opened_at.localeCompare(b.opened_at);
}

/** "951-555-0100", for a card. */
export const dialable = (p: string | null): string =>
  p && p.length === 10 ? `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}` : "";
